// Reception-desk timestamp formatter that emits *Arabic-Indic digits* via
// `Intl.DateTimeFormat('ar', ...)` (e.g. "٢٠٢٦/٠٤/٢٨ ٠٣:٠٥ مساءً").
// For Latin-digit Arabic output used elsewhere (sales, payments, expenses),
// see `formatLocalizedDateTime` in `localizedFormatters.js`.

import i18n from '@/i18n';
import { getTenantDisplayTimeZone } from '@/utils/localizedFormatters';

function getArabicMeridiem(hour24) {
  return hour24 < 12 ? i18n.t('generalMessages.meridiem.am') : i18n.t('generalMessages.meridiem.pm');
}

function parseUtcTimestamp(datetime) {
  if (!datetime) return null;
  if (datetime instanceof Date) return Number.isNaN(datetime.getTime()) ? null : datetime;

  const value = String(datetime).trim();
  if (!value) return null;

  const hasTimeZone = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
  if (hasTimeZone) {
    const dateWithZone = new Date(value);
    return Number.isNaN(dateWithZone.getTime()) ? null : dateWithZone;
  }

  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );

  if (!match) {
    const fallbackDate = new Date(value);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }

  const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
  return new Date(Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ));
}

export function formatReceptionArabicTimestamp(datetime) {
  const date = parseUtcTimestamp(datetime);
  if (!date) return 'NA';

  const formatter = new Intl.DateTimeFormat('ar', {
    timeZone: getTenantDisplayTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const partMap = parts.reduce((accumulator, part) => {
    if (part.type !== 'literal') {
      accumulator[part.type] = part.value;
    }
    return accumulator;
  }, {});

  const hour24 = Number(partMap.hour);
  const hour12 = String(hour24 % 12 || 12).padStart(2, '0');

  return `${partMap.year}/${partMap.month}/${partMap.day} ${hour12}:${partMap.minute} ${getArabicMeridiem(hour24)}`;
}
